import React from "react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const UL = ({ children }) => <ul className="space-y-2 pl-1">{children}</ul>;
const LI = ({ children }) => (
  <li className="flex gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-decide-forest/70" /><span>{children}</span></li>
);

const SECTIONS = [
  { id: "how-to-delete", title: "How to Delete Your Account" },
  { id: "what-is-deleted", title: "What Data Is Deleted" },
  { id: "what-is-retained", title: "What Data Is Retained" },
  { id: "timing", title: "Timing" },
  { id: "contact", title: "Contact" },
];

export default function DeleteAccount() {
  return (
    <LegalLayout
      metaTitle="DECIDE — Delete Your Account & Data"
      metaDescription="How to permanently delete your DECIDE account and associated data, and what data is deleted or retained."
      title="Delete Your Account"
      subtitle="How to permanently delete your DECIDE account and the data associated with it."
      updated="22 August 2026"
      toc={SECTIONS}
    >
      <div data-testid="delete-account-page">
        <p className="mb-8 text-[15.5px] leading-[1.75] text-decide-slate">
          DECIDE is developed by <strong className="text-decide-ink font-semibold">DGG Labs</strong>. This page explains how you can
          request deletion of your DECIDE account and describes what data is removed and what, if anything, is retained.
        </p>

        <LegalSection id="how-to-delete" index={1} title="How to Delete Your Account">
          <p>
            You can permanently delete your account and all associated personal data directly inside the app, at any time,
            without contacting us:
          </p>
          <div className="rounded-2xl border border-decide-line bg-decide-surface p-5" data-testid="delete-steps">
            <ol className="space-y-3">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-decide-forest text-[13px] font-semibold text-white">1</span>
                <span>Open the DECIDE app and sign in with the account you want to delete.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-decide-forest text-[13px] font-semibold text-white">2</span>
                <span>Go to <strong className="text-decide-ink font-semibold">Settings</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-decide-forest text-[13px] font-semibold text-white">3</span>
                <span>Tap <strong className="text-decide-ink font-semibold">Delete account</strong> and confirm.</span>
              </li>
            </ol>
          </div>
          <p>
            Once confirmed, deletion is immediate and permanent — it cannot be undone. If you are unable to access the app,
            you can also request deletion by email (see <a className="text-decide-forest underline underline-offset-2" href="#contact">Contact</a> below).
          </p>
        </LegalSection>

        <LegalSection id="what-is-deleted" index={2} title="What Data Is Deleted">
          <p>Deleting your account permanently removes the following from our systems:</p>
          <UL>
            <LI><strong className="text-decide-ink font-semibold">Your account and profile</strong> — your email address, name, profile picture, and account identifier.</LI>
            <LI><strong className="text-decide-ink font-semibold">Your decisions and history</strong> — every decision you created, including the situation you described, options and factors, your answers, the AI analysis and recommendation, any decision reviews (outcome tracking), and saved next steps.</LI>
            <LI><strong className="text-decide-ink font-semibold">Your preferences and usage</strong> — your language and theme settings, subscription status, and free-usage counter.</LI>
            <LI><strong className="text-decide-ink font-semibold">Your subscription and payment records</strong> stored in our database, and any active subscription is cancelled with our payment processor (Stripe).</LI>
            <LI><strong className="text-decide-ink font-semibold">Your active sessions</strong> — you are signed out on all devices.</LI>
          </UL>
        </LegalSection>

        <LegalSection id="what-is-retained" index={3} title="What Data Is Retained">
          <p>
            We keep <strong className="text-decide-ink font-semibold">no personal data that identifies you</strong> after your account is
            deleted. In limited cases, some non-identifying or externally held records may persist:
          </p>
          <UL>
            <LI><strong className="text-decide-ink font-semibold">Payment records held by Stripe.</strong> Our payment processor (Stripe) may independently retain transaction records to meet its own legal, tax, and accounting obligations. These are held by Stripe, not in your DECIDE account.</LI>
            <LI><strong className="text-decide-ink font-semibold">AI-content reports.</strong> If you reported an AI response, that report is stored without any link to your identity and may be retained for safety and abuse-prevention purposes.</LI>
            <LI><strong className="text-decide-ink font-semibold">Routine server logs.</strong> Standard security and diagnostic logs (e.g., IP address and request metadata) may persist for a short period as part of normal, secure operation and are not used to reconstruct your account.</LI>
          </UL>
          <p>None of the above can be used to restore your deleted account or your decisions.</p>
        </LegalSection>

        <LegalSection id="timing" index={4} title="Timing">
          <p>
            Deletion of your account, decisions, preferences, subscription records, and sessions happens{" "}
            <strong className="text-decide-ink font-semibold">immediately</strong> when you confirm. Any retained records described
            above are removed in line with the applicable legal retention periods.
          </p>
        </LegalSection>

        <LegalSection id="contact" index={5} title="Contact">
          <p>If you have questions about account deletion or need help deleting your data, contact us:</p>
          <div className="rounded-2xl border border-decide-line bg-decide-surface p-5 text-[15px] leading-relaxed">
            <p className="font-semibold text-decide-ink">DECIDE</p>
            <p className="mt-1">Operator: DGG Labs</p>
            <p className="mt-1">Email: <a className="text-decide-forest underline underline-offset-2" href="mailto:dgg.operations@gmail.com" data-testid="delete-contact-email">dgg.operations@gmail.com</a></p>
          </div>
          <p className="text-[14px] text-decide-mute">
            See also our <a className="text-decide-forest underline underline-offset-2" href="/privacy">Privacy Policy</a> for full details on how we handle your data.
          </p>
        </LegalSection>
      </div>
    </LegalLayout>
  );
}
